import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Plus, Edit2, Trash2, Eye, Package, LogOut, ShoppingBag,
  Upload, X, Clock, CheckCircle, XCircle, ImageIcon, Camera,
  RefreshCw, AlertTriangle, CalendarClock
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import Layout from "@/components/layout/Layout";
import axios from "axios";
import { API } from "@/App";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith("http")) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  return url.startsWith("/uploads/") ? `${backendUrl}/api${url}` : `${backendUrl}${url}`;
};

const CATEGORIES = [
  { id: "vehiculos", name: "Veh\u00edculos" }, { id: "inmuebles", name: "Inmuebles" },
  { id: "electronica", name: "Electr\u00f3nica" }, { id: "hogar", name: "Hogar y Muebles" },
  { id: "ropa", name: "Ropa y Accesorios" }, { id: "servicios", name: "Servicios" },
  { id: "empleo", name: "Empleo" }, { id: "mascotas", name: "Mascotas" },
  { id: "deportes", name: "Deportes" }, { id: "otros", name: "Otros" },
];

const statusConfig = {
  pending: { label: "Pendiente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
  approved: { label: "Activo", color: "bg-green-100 text-green-700", icon: CheckCircle },
  rejected: { label: "Rechazado", color: "bg-red-100 text-red-700", icon: XCircle },
  expired: { label: "Vencido", color: "bg-orange-100 text-orange-700", icon: AlertTriangle },
};

// Helper to format remaining days
const formatExpiration = (expiresAt) => {
  if (!expiresAt) return null;
  const now = new Date();
  const expDate = new Date(expiresAt);
  const diffMs = expDate - now;
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  
  if (diffDays < 0) return { text: "Vencido", expired: true };
  if (diffDays === 0) return { text: "Vence hoy", expired: false, warning: true };
  if (diffDays === 1) return { text: "Vence mañana", expired: false, warning: true };
  if (diffDays <= 3) return { text: `Vence en ${diffDays} días`, expired: false, warning: true };
  return { text: `${diffDays} días restantes`, expired: false };
};

const initialForm = {
  title: "", description: "", price: 0, currency: "HNL", category: "otros",
  condition: "nuevo", contact_method: "whatsapp", delivery_location: "", photo_urls: [],
};

export default function MarketplaceDashboard() {
  const [user, setUser] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const token = localStorage.getItem("mp_token");

  const mpHeaders = () => ({ headers: { Authorization: `Bearer ${token}` } });

  useEffect(() => {
    if (!token) { navigate("/marketplace/registro"); return; }
    fetchData();
  }, [token]);

  const fetchData = async () => {
    try {
      const [meRes, prodRes] = await Promise.all([
        axios.get(`${API}/marketplace/me`, mpHeaders()),
        axios.get(`${API}/marketplace/products`, mpHeaders()),
      ]);
      setUser(meRes.data);
      setProducts(prodRes.data.items || []);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("mp_token");
        localStorage.removeItem("mp_user");
        navigate("/marketplace/registro");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("mp_token");
    localStorage.removeItem("mp_user");
    navigate("/marketplace");
  };

  const handleNew = () => {
    setSelected(null);
    setForm(initialForm);
    setDialogOpen(true);
  };

  const handleEdit = (p) => {
    setSelected(p);
    setForm({
      title: p.title, description: p.description || "", price: p.price || 0, currency: p.currency || "HNL",
      category: p.category || "otros", condition: p.condition || "nuevo",
      contact_method: p.contact_method || "whatsapp", delivery_location: p.delivery_location || "",
      photo_urls: p.photo_urls || [],
    });
    setDialogOpen(true);
  };

  const handleUploadPhotos = async (e) => {
    const files = Array.from(e.target.files);
    if (form.photo_urls.length + files.length > 10) {
      toast.error("M\u00e1ximo 10 fotos por producto");
      return;
    }
    setUploading(true);
    try {
      const urls = [...form.photo_urls];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const res = await axios.post(`${API}/marketplace/upload-photo`, fd, mpHeaders());
        urls.push(res.data.url);
      }
      setForm({ ...form, photo_urls: urls });
      toast.success(`${files.length} foto(s) subida(s)`);
    } catch { toast.error("Error al subir fotos"); }
    finally { setUploading(false); }
  };

  const removePhoto = (index) => {
    const updated = form.photo_urls.filter((_, i) => i !== index);
    setForm({ ...form, photo_urls: updated });
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("T\u00edtulo obligatorio"); return; }
    setSaving(true);
    try {
      if (selected) {
        await axios.put(`${API}/marketplace/products/${selected.product_id}`, form, mpHeaders());
        toast.success("Producto actualizado (pendiente de aprobaci\u00f3n)");
      } else {
        await axios.post(`${API}/marketplace/products`, form, mpHeaders());
        toast.success("Producto publicado (pendiente de aprobaci\u00f3n)");
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (!selected) return;
    try {
      await axios.delete(`${API}/marketplace/products/${selected.product_id}`, mpHeaders());
      toast.success("Producto eliminado");
      setDeleteOpen(false);
      fetchData();
    } catch { toast.error("Error al eliminar"); }
  };

  const handleReactivate = async (productId) => {
    try {
      await axios.put(`${API}/marketplace/products/${productId}/reactivate`, {}, mpHeaders());
      toast.success("Producto reactivado (pendiente de aprobación)");
      fetchData();
    } catch (err) { 
      toast.error(err.response?.data?.detail || "Error al reactivar"); 
    }
  };

  if (loading) return (
    <Layout><div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-10 w-10 border-[3px] border-emerald-600 border-t-transparent" /></div></Layout>
  );

  const pending = products.filter(p => p.status === "pending").length;
  const approved = products.filter(p => p.status === "approved" && !p.is_expired).length;
  const expired = products.filter(p => p.is_expired).length;

  return (
    <Layout>
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-gray-900">Mis Productos</h1>
            <p className="text-gray-500 mt-1">Hola, {user?.name}</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={handleNew} className="rounded-xl bg-emerald-600 hover:bg-emerald-500" data-testid="new-product-btn">
              <Plus className="h-4 w-4 mr-2" /> Nuevo Producto
            </Button>
            <Button variant="ghost" onClick={handleLogout} className="text-gray-500" data-testid="logout-btn">
              <LogOut className="h-4 w-4 mr-1" /> Salir
            </Button>
          </div>
        </div>
      </section>

      <section className="py-8 bg-gray-50" data-testid="seller-dashboard">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-2xl font-bold text-gray-900">{products.length}</p>
              <p className="text-xs text-gray-400">Total</p>
            </div>
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-2xl font-bold text-green-600">{approved}</p>
              <p className="text-xs text-gray-400">Activos</p>
            </div>
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-2xl font-bold text-yellow-600">{pending}</p>
              <p className="text-xs text-gray-400">Pendientes</p>
            </div>
            <div className="bg-white rounded-xl p-4 border text-center">
              <p className="text-2xl font-bold text-orange-600">{expired}</p>
              <p className="text-xs text-gray-400">Vencidos</p>
            </div>
          </div>

          {products.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-2xl border">
              <Package className="h-16 w-16 text-gray-200 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-700 mb-2">A&uacute;n no tienes productos</h3>
              <p className="text-gray-400 mb-6">Publica tu primer producto para empezar a vender</p>
              <Button onClick={handleNew} className="rounded-xl bg-emerald-600 hover:bg-emerald-500">
                <Plus className="h-4 w-4 mr-2" /> Publicar producto
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {products.map(p => {
                // Handle expired status
                const isExpired = p.is_expired;
                const effectiveStatus = isExpired ? "expired" : p.status;
                const sc = statusConfig[effectiveStatus] || statusConfig.pending;
                const StatusIcon = sc.icon;
                const expInfo = p.expires_at ? formatExpiration(p.expires_at) : null;
                
                return (
                  <div key={p.product_id} className={`bg-white rounded-xl border overflow-hidden ${isExpired ? 'opacity-75' : ''}`} data-testid={`my-product-${p.product_id}`}>
                    <div className="aspect-[16/10] bg-gray-100 relative">
                      {p.photo_urls?.[0] ? (
                        <img src={getImageUrl(p.photo_urls[0])} alt={p.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center"><ImageIcon className="h-10 w-10 text-gray-300" /></div>
                      )}
                      <Badge className={`absolute top-3 left-3 ${sc.color} text-xs`}>
                        <StatusIcon className="h-3 w-3 mr-1" />{sc.label}
                      </Badge>
                    </div>
                    <div className="p-4">
                      <h3 className="font-semibold text-gray-900 truncate">{p.title}</h3>
                      <p className="text-lg font-bold text-emerald-700 mt-1">L {(p.price || 0).toLocaleString("es-HN")}</p>
                      <div className="flex items-center justify-between mt-1">
                        <div className="flex items-center gap-1 text-xs text-gray-400">
                          <Eye className="h-3 w-3" /> {p.views || 0} visitas
                        </div>
                        {expInfo && p.status === "approved" && (
                          <div className={`flex items-center gap-1 text-xs ${expInfo.expired ? 'text-orange-600' : expInfo.warning ? 'text-yellow-600' : 'text-gray-400'}`}>
                            <CalendarClock className="h-3 w-3" /> {expInfo.text}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-3 pt-3 border-t">
                        {isExpired ? (
                          <Button 
                            variant="outline" 
                            size="sm" 
                            className="flex-1 rounded-lg text-orange-600 border-orange-300 hover:bg-orange-50" 
                            onClick={() => handleReactivate(p.product_id)}
                            data-testid={`reactivate-${p.product_id}`}
                          >
                            <RefreshCw className="h-3.5 w-3.5 mr-1" /> Reactivar
                          </Button>
                        ) : (
                          <Button variant="outline" size="sm" className="flex-1 rounded-lg" onClick={() => handleEdit(p)} data-testid={`edit-${p.product_id}`}>
                            <Edit2 className="h-3.5 w-3.5 mr-1" /> Editar
                          </Button>
                        )}
                        <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700 rounded-lg" onClick={() => { setSelected(p); setDeleteOpen(true); }}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selected ? "Editar Producto" : "Nuevo Producto"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label>T&iacute;tulo del producto *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ej: iPhone 15 Pro Max" data-testid="prod-title" />
            </div>
            <div>
              <Label>Descripci&oacute;n</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe tu producto..." rows={3} data-testid="prod-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Precio (Lempiras)</Label>
                <Input type="number" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} placeholder="0" data-testid="prod-price" />
              </div>
              <div>
                <Label>Categor&iacute;a</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="prod-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Condici&oacute;n</Label>
                <Select value={form.condition} onValueChange={(v) => setForm({ ...form, condition: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nuevo">Nuevo</SelectItem>
                    <SelectItem value="usado">Usado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Contacto preferido</Label>
                <Select value={form.contact_method} onValueChange={(v) => setForm({ ...form, contact_method: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                    <SelectItem value="llamada">Llamada</SelectItem>
                    <SelectItem value="ambos">Ambos</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Ubicaci&oacute;n de entrega</Label>
              <Input value={form.delivery_location} onChange={(e) => setForm({ ...form, delivery_location: e.target.value })} placeholder="Ej: Choluteca centro, env&iacute;o a todo el pa&iacute;s" data-testid="prod-delivery" />
            </div>

            {/* Photos */}
            <div>
              <Label>Fotos ({form.photo_urls.length}/10)</Label>
              <div className="grid grid-cols-5 gap-2 mt-2">
                {form.photo_urls.map((url, i) => (
                  <div key={i} className="relative aspect-square rounded-lg overflow-hidden border">
                    <img src={getImageUrl(url)} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => removePhoto(i)} className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {form.photo_urls.length < 10 && (
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="aspect-square rounded-lg border-2 border-dashed border-gray-300 flex flex-col items-center justify-center text-gray-400 hover:border-emerald-400 hover:text-emerald-500 transition-colors"
                    data-testid="upload-photo-btn"
                  >
                    {uploading ? <div className="animate-spin rounded-full h-5 w-5 border-2 border-emerald-500 border-t-transparent" /> : <><Camera className="h-5 w-5 mb-1" /><span className="text-[10px]">Agregar</span></>}
                  </button>
                )}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleUploadPhotos} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-500" data-testid="save-product-btn">
              {saving ? "Guardando..." : selected ? "Actualizar" : "Publicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar producto</AlertDialogTitle>
            <AlertDialogDescription>&iquest;Est&aacute;s seguro de eliminar "{selected?.title}"?</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Layout>
  );
}
